import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { faHome } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import ListBlankState from '@components/ListBlankState'
import PageHeader from '@components/PageHeader'

import { groupEntitiesByEdition } from '@functions/publicationEdition'
import { sanitiseObject } from '@functions/utility'

import { generateUrls } from '@hooks/usePublicationInspection'
import usePublicationInspections from '@hooks/usePublicationInspections'
import useSelector from '@hooks/useSelector'
import useStatuses from '@hooks/useStatuses'

import type { PublicationInspectionsFilters } from '@hooks/usePublicationInspections'

import NewsDeskFilters from '@sections/Admin/packs/NewsDesk/components/NewsDeskFilters'

import PublicationInspectionGroup from '../PublicationInspectionGroup'

const allowedFilters = ['brand_id', 'publication_id', 'publication_edition_id', 'status_id', 'string']

interface PageContentProps {
  pageFilters: PublicationInspectionsFilters,
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  useLocation()

  const sanitisedFilters = sanitiseObject(pageFilters, allowedFilters)

  const publicationInspectionsPayload = usePublicationInspections({
    filters: {
      ...sanitisedFilters,
    },
    performHttpRequests: true,
    requestOptions: {
      // @ts-expect-error TS2353
      include_campaign_agency: true,
      include_publication_edition_publication: true,
      include_publication_inspection_campaign: true,
      include_publication_inspection_edition: true,
    },
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredPublicationInspections,
    filteredPublicationInspectionsCount,
    hasFilteredPublicationInspections,
    loading,
  } = publicationInspectionsPayload

  const statusesPayload = useStatuses({ filters: { status_keys: ['approved', 'pending'] } })
  const { filteredStatuses } = statusesPayload

  const { campaigns, publicationEditions } = useSelector(reduxState => reduxState.entities)

  const groupedInspectionsByEdition = groupEntitiesByEdition({
    callbacks: {
      getPrimaryGroupId: entity => entity.publication_edition_id,
      getSecondaryGroupId: entity => campaigns[entity.campaign_id]?.agency_id,
    },
    entities: filteredPublicationInspections,
    publicationEditions,
    secondaryGroupKey: 'agencies',
  })

  const { inspectionsUrl } = generateUrls()

  return (
    <>
      <Helmet>
        <title>Inspections | Realhub</title>
      </Helmet>

      <PageHeader
        activeTabBarItemKey="inspections"
        nestedNavigation
        tabBarItems={[
          {
            href: inspectionsUrl,
            icon: faHome,
            key: 'inspections',
            title: 'INSPECTIONS',
          },
        ]}
        title="Inspections"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredPublicationInspections && <ListBlankState />}

            {!loading && hasFilteredPublicationInspections && (
              Object.values(groupedInspectionsByEdition).map(groupedInspection => (
                <PublicationInspectionGroup
                  entityCount={groupedInspection.entityCount}
                  agencyGroups={groupedInspection.agencies}
                  key={groupedInspection.id}
                  publicationEditionId={groupedInspection.id}
                />
              ))
            )}

            {!loading && (
              <Box variant="white">
                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredPublicationInspectionsCount}
                  loading={loading}
                  showBorderTop={false}
                />
              </Box>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NewsDeskFilters
              allowedFilters={allowedFilters}
              pageFilters={pageFilters}
              statuses={filteredStatuses}
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
